import { EventTypeAutoTranslatedField } from "@calcom/prisma/enums";

export type EventTypeTranslationEntry = {
  field: EventTypeAutoTranslatedField;
  fieldKey?: string | null;
  optionKey?: string | null;
  targetLocale: string;
  translatedText: string;
};

type TranslationKey = {
  field: EventTypeAutoTranslatedField;
  fieldKey?: string;
  optionKey?: string;
};

type TranslatableSubField = { name: string; label?: string; placeholder?: string };

type TranslatableBookingField = {
  name: string;
  type?: string;
  label?: string;
  placeholder?: string;
  options?: { label: string; value: string }[];
  variantsConfig?: { variants: Record<string, { fields: TranslatableSubField[] }> };
};

export const getBaseLocale = (locale: string): string => locale.split(/[-_]/)[0].toLowerCase();

const matchesKey = (entry: EventTypeTranslationEntry, key: TranslationKey) =>
  entry.field === key.field &&
  (entry.fieldKey ?? "") === (key.fieldKey ?? "") &&
  (entry.optionKey ?? "") === (key.optionKey ?? "");

/**
 * Returns the translation for `locale`, trying the exact locale before its base language.
 * Blank translations count as missing, so callers can always fall back to the original text.
 */
export function findTranslation(
  entries: EventTypeTranslationEntry[] | null | undefined,
  key: TranslationKey,
  locale: string | null | undefined
): string | undefined {
  if (!entries?.length || !locale) return undefined;

  const candidates = entries.filter((entry) => matchesKey(entry, key) && entry.translatedText.trim() !== "");
  const exact = candidates.find((entry) => entry.targetLocale === locale);
  if (exact) return exact.translatedText;

  const base = getBaseLocale(locale);
  return candidates.find((entry) => getBaseLocale(entry.targetLocale) === base)?.translatedText;
}

export function translateEventTypeText(
  entries: EventTypeTranslationEntry[] | null | undefined,
  locale: string | null | undefined,
  original: { title: string; description?: string | null }
): { title: string; description: string | null } {
  return {
    title: findTranslation(entries, { field: EventTypeAutoTranslatedField.TITLE }, locale) ?? original.title,
    description:
      findTranslation(entries, { field: EventTypeAutoTranslatedField.DESCRIPTION }, locale) ??
      original.description ??
      null,
  };
}

/** Sub-fields such as the name question's "full name" are keyed `<field>.<sub-field>`. */
function translateVariants(
  field: TranslatableBookingField,
  entries: EventTypeTranslationEntry[],
  locale: string
): TranslatableBookingField["variantsConfig"] {
  if (!field.variantsConfig) return undefined;
  let translated = false;
  const variants = Object.fromEntries(
    Object.entries(field.variantsConfig.variants).map(([variantName, variant]) => [
      variantName,
      {
        ...variant,
        fields: variant.fields.map((subField) => {
          const fieldKey = `${field.name}.${subField.name}`;
          const label = subField.label
            ? findTranslation(
                entries,
                { field: EventTypeAutoTranslatedField.BOOKING_FIELD_LABEL, fieldKey },
                locale
              )
            : undefined;
          const placeholder = subField.placeholder
            ? findTranslation(
                entries,
                { field: EventTypeAutoTranslatedField.BOOKING_FIELD_PLACEHOLDER, fieldKey },
                locale
              )
            : undefined;
          if (!label && !placeholder) return subField;
          translated = true;
          return { ...subField, ...(label ? { label } : {}), ...(placeholder ? { placeholder } : {}) };
        }),
      },
    ])
  );
  return translated ? { ...field.variantsConfig, variants } : undefined;
}

/**
 * Replaces the visible texts of booking questions. Names and option values stay untouched,
 * because responses, integrations and saved bookings key on them.
 */
export function translateBookingFields<T extends TranslatableBookingField>(
  fields: T[],
  entries: EventTypeTranslationEntry[] | null | undefined,
  locale: string | null | undefined
): T[] {
  if (!entries?.length || !locale) return fields;

  return fields.map((field) => {
    const label = field.label
      ? findTranslation(
          entries,
          { field: EventTypeAutoTranslatedField.BOOKING_FIELD_LABEL, fieldKey: field.name },
          locale
        )
      : undefined;
    const placeholder = field.placeholder
      ? findTranslation(
          entries,
          { field: EventTypeAutoTranslatedField.BOOKING_FIELD_PLACEHOLDER, fieldKey: field.name },
          locale
        )
      : undefined;
    // A radio answer is saved as the option's label, so its options keep their original labels
    const options =
      field.type === "radio"
        ? undefined
        : field.options?.map((option) => ({
            ...option,
            label:
              findTranslation(
                entries,
                {
                  field: EventTypeAutoTranslatedField.BOOKING_FIELD_OPTION_LABEL,
                  fieldKey: field.name,
                  optionKey: option.value,
                },
                locale
              ) ?? option.label,
          }));

    const variantsConfig = translateVariants(field, entries, locale);

    if (!label && !placeholder && !options && !variantsConfig) return field;

    return {
      ...field,
      ...(label ? { label } : {}),
      ...(placeholder ? { placeholder } : {}),
      ...(options ? { options } : {}),
      ...(variantsConfig ? { variantsConfig } : {}),
    };
  });
}
